import React, { useRef, useEffect } from "react";
import Editor from "@monaco-editor/react";
import {
  getMonacoOptions,
  configureMonacoBeforeMount,
  getMonacoTheme,
  configureMonacoAfterMount,
  getLanguageSpecificOptions,
} from "../../utils/monacoConfig";
import { getLanguageFromFileName } from "../../utils/fileUtils";
import { EDITOR_HEIGHT } from "../../constants";

/**
 * Monaco Editor wrapper component
 * @param {Object} props - Component props
 * @param {string} props.fileName - Name of the file being edited
 * @param {string} props.content - File content
 * @param {Function} props.onChange - Content change handler
 * @param {boolean} props.isDarkMode - Whether dark mode is enabled
 * @param {string} props.height - Editor height (optional)
 * @param {Function} props.onSave - Save handler (optional)
 * @param {boolean} props.isReadOnly - Whether editor is in read-only mode
 * @returns {JSX.Element} The Monaco editor
 */
const MonacoEditor = ({
  fileName,
  content,
  onChange,
  isDarkMode,
  height = EDITOR_HEIGHT,
  onSave,
  isReadOnly = false,
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  // Handle editor mount
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Configure editor after mount
    configureMonacoAfterMount(editor, monaco);

    // Add save keybinding if handler provided
    if (onSave) {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () =>
        onSave(),
      );
    }

    // Apply language-specific options
    const language = getLanguageFromFileName(fileName);
    const languageOptions = getLanguageSpecificOptions(language);
    editor.updateOptions(languageOptions);
  };

  // Update language-specific options when file changes
  useEffect(() => {
    if (editorRef.current && fileName) {
      const language = getLanguageFromFileName(fileName);
      const languageOptions = getLanguageSpecificOptions(language);
      editorRef.current.updateOptions(languageOptions);
    }
  }, [fileName]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        editorRef.current.dispose();
      }
    };
  }, []);

  const language = getLanguageFromFileName(fileName);
  const theme = getMonacoTheme(isDarkMode);
  const options = {
    ...getMonacoOptions(isDarkMode),
    readOnly: isReadOnly,
  };

  return (
    <Editor
      height={height}
      language={language}
      value={content}
      onChange={onChange}
      theme={theme}
      options={options}
      beforeMount={configureMonacoBeforeMount}
      onMount={handleEditorDidMount}
    />
  );
};

export default MonacoEditor;
